import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersApi } from '../../api/users.api';
import { useUIStore } from '../../store';
import NotificationMenu from './NotificationMenu';
import { resolveUserAvatar } from '../../utils/media';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
} from '@mui/material';
import { Menu as MenuIcon, Bell, User, Settings, LogOut } from 'lucide-react';

const resolveAvatarSrc = (value) => resolveUserAvatar(value);
const NOTIFICATION_POLL_INTERVAL_MS = 40000;

const loadStoredSession = () => {
  try {
    const stored = localStorage.getItem('session');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Failed to read stored session:', error);
    return null;
  }
};

const Header = ({ drawerWidth, collapsedWidth = 70, onDrawerToggle, isMobile, collapsed, session }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const { unreadNotifications, setNotifications, showError } = useUIStore();
  const [avatarPath, setAvatarPath] = useState(() => {
    const storedSession = loadStoredSession();
    return session?.u_image || session?.image || storedSession?.u_image || storedSession?.image || '';
  });

  useEffect(() => {
    let isActive = true;
    let hasLoadedOnce = false;
    let pollTimer = null;

    const loadNotifications = async () => {
      try {
        const response = await usersApi.getNotifications();
        const payload = response?.data?.data ?? response?.data ?? [];
        const list = Array.isArray(payload) ? payload : [];
        const mapped = list.map((item) => ({
          id: item.id,
          title: item.title || 'Notification',
          message: item.message || '',
          type: item.category || item.sender_type || 'notification',
          read: Number(item.is_read) === 1,
          createdAt: item.created_at || item.createdAt || item.sent_at || new Date().toISOString(),
        }));

        if (isActive) {
          setNotifications(mapped);
          hasLoadedOnce = true;
        }
      } catch (error) {
        if (isActive) {
          console.error('Failed to load notifications:', error);
          if (!hasLoadedOnce) {
            showError('Failed to load notifications');
          }
        }
      }
    };

    loadNotifications();
    pollTimer = setInterval(loadNotifications, NOTIFICATION_POLL_INTERVAL_MS);
    return () => {
      isActive = false;
      if (pollTimer) {
        clearInterval(pollTimer);
      }
    };
  }, [setNotifications, showError]);

  useEffect(() => {
    const storedSession = loadStoredSession();
    setAvatarPath(
      session?.u_image || session?.image || storedSession?.u_image || storedSession?.image || ''
    );
  }, [session?.u_image, session?.image]);

  useEffect(() => {
    const handleSessionUpdate = (event) => {
      const nextSession = event?.detail?.session || event?.detail || loadStoredSession();
      setAvatarPath(nextSession?.u_image || nextSession?.image || '');
    };

    window.addEventListener('session:updated', handleSessionUpdate);
    return () => window.removeEventListener('session:updated', handleSessionUpdate);
  }, []);

  const avatarSrc = useMemo(() => resolveAvatarSrc(avatarPath), [avatarPath]);

  const handleLogout = () => {
    localStorage.removeItem('session');
    localStorage.removeItem('token');
    navigate('/login');
    window.location.reload();
  };

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationOpen = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const currentSidebarWidth = collapsed ? collapsedWidth : drawerWidth;

  return (
    <AppBar
      position="fixed"
      elevation={1}
      sx={{
        left: isMobile ? 0 : `${currentSidebarWidth}px`,
        width: isMobile ? '100%' : `calc(100% - ${currentSidebarWidth}px)`,
        bgcolor: 'background.paper',
        color: 'text.primary',
        transition: 'left 0.3s ease, width 0.3s ease',
        zIndex: (theme) => theme.zIndex.drawer - 1,
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: 70 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isMobile && (
            <Box
              component="img"
              src="/assets/images/chat_logo.png"
              alt="ChattyBO"
              sx={{ height: 36 }}
            />
          )}

          <IconButton
            onClick={onDrawerToggle}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' },
            }}
          >
            <MenuIcon size={24} />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton
            onClick={handleNotificationOpen}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main', bgcolor: 'grey.100' },
            }}
          >
            <Badge
              badgeContent={unreadNotifications}
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: 10,
                  minWidth: 16,
                  height: 16,
                },
              }}
            >
              <Bell size={22} />
            </Badge>
          </IconButton>

          <NotificationMenu
            anchorEl={notificationAnchor}
            open={Boolean(notificationAnchor)}
            onClose={handleNotificationClose}
          />

          <IconButton
            onClick={handleUserMenuOpen}
            sx={{
              p: 0.5,
              borderRadius: 2,
              '&:hover': { bgcolor: 'grey.100' },
            }}
          >
            <Avatar
              src={avatarSrc}
              alt="User"
              sx={{
                width: 36,
                height: 36,
                border: '2px solid',
                borderColor: '#ffffff',
              }}
            />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: { mt: 1, minWidth: 180 },
            }}
          >
            <MenuItem
              onClick={() => {
                handleUserMenuClose();
                navigate('/settings/profile');
              }}
            >
              <ListItemIcon>
                <User size={16} />
              </ListItemIcon>
              <ListItemText primary="Profile" />
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleUserMenuClose();
                navigate('/settings');
              }}
            >
              <ListItemIcon>
                <Settings size={16} />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={handleLogout}
              sx={{
                color: 'error.main',
                '&:hover': { bgcolor: 'error.lighter' },
              }}
            >
              <ListItemIcon>
                <LogOut size={16} color="inherit" />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
